import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { getValidTimezone } from "../../utils/dateFormatter";
import { BarChart } from "../Charts/BarChart";
import MethanolBarChart from "./MethanolBarChart";
const BAR_COLOR = "#1AC69E";
const ENERGY_UNIT = "kWh";
const DATE_PAD_LENGTH = 2;
const DATE_PAD_CHAR = "0";
const DATE_PART_LENGTH = 10;
const ISO_DATE_SEPARATOR = "T";
const NA_VALUE = "N/A";
const DEFAULT_TIMEZONE = "UTC";
const DEFAULT_X_AXIS_LABEL = "Days";
const NO_DATA_HEIGHT = "h-[245px]";
const Y_AXIS_TICK_COUNT = 5;
const MIN_MAX_ENERGY = 1;
const MIN_STEP_SIZE = 1;
// ── Helpers ───────────────────────────────────────────────────
const formatDateMMDD = (date) => {
    const month = String(date.getUTCMonth() + 1).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
    const day = String(date.getUTCDate()).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
    return `${month}/${day}`;
};
const formatTooltipDate = (date, timezone) => {
    const safeTimezone = getValidTimezone(timezone) ?? "UTC";
    const monthName = date.toLocaleString("en-US", { month: "long", timeZone: safeTimezone });
    const day = date.toLocaleString("en-US", { day: "numeric", timeZone: safeTimezone });
    return `${day} ${monthName}`;
};
// ── Fill missing dates with N/A ───────────────────────────────
export const fillMissingDates = (data, startDate, endDate) => {
    const result = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);
    while (currentDate <= end) {
        const existing = data.find((item) => new Date(item.date).getTime() === currentDate.getTime());
        if (existing) {
            result.push(existing);
        }
        else {
            result.push({
                date: currentDate.toISOString().split(ISO_DATE_SEPARATOR)[0],
                energyDeliveredToSite: NA_VALUE
            });
        }
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
    return result;
};
// ── Component ─────────────────────────────────────────────────
const MethanolEnergyBarChart = ({ fromDate, legendLabel, showDischargeEnergy = false, statDetails, timezone = DEFAULT_TIMEZONE, toDate, xAxisLabel = DEFAULT_X_AXIS_LABEL }) => {
    if (!statDetails.length) {
        return (_jsx("div", { className: `flex justify-center items-center ${NO_DATA_HEIGHT}`, "data-test": "methanol-energy-no-data", children: "No data available" }));
    }
    // Transform API data into the chart-friendly shape
    const sampleInfo = statDetails.map((d) => ({
        date: d.startTimestamp.substring(0, DATE_PART_LENGTH),
        energyDeliveredToSite: d.energyDetails?.chargeEnergy?.value ?? 0
    }));
    const dischargeInfo = showDischargeEnergy
        ? statDetails.map((d) => ({
            date: d.startTimestamp.substring(0, DATE_PART_LENGTH),
            energyDeliveredToSite: d.energyDetails?.dischargeEnergy?.value ?? 0
        }))
        : undefined;
    // Use the selected date range; fall back to min/max of data if not provided
    const startDateStr = fromDate
        ? fromDate.substring(0, DATE_PART_LENGTH)
        : new Date(sampleInfo.reduce((min, item) => Math.min(min, new Date(item.date).getTime()), new Date(sampleInfo[0].date).getTime()))
            .toISOString()
            .split(ISO_DATE_SEPARATOR)[0];
    const endDateStr = toDate
        ? toDate.substring(0, DATE_PART_LENGTH)
        : new Date(sampleInfo.reduce((max, item) => Math.max(max, new Date(item.date).getTime()), new Date(sampleInfo[0].date).getTime()))
            .toISOString()
            .split(ISO_DATE_SEPARATOR)[0];
    const filledData = fillMissingDates(sampleInfo, startDateStr, endDateStr);
    const filledDischargeData = dischargeInfo ? fillMissingDates(dischargeInfo, startDateStr, endDateStr) : undefined;
    const maxEnergy = Math.max(MIN_MAX_ENERGY, ...sampleInfo.map((item) => Number(item.energyDeliveredToSite)), ...(dischargeInfo?.map((item) => Number(item.energyDeliveredToSite)) ?? []));
    const yAxisStepSize = Math.ceil(maxEnergy / Y_AXIS_TICK_COUNT) || MIN_STEP_SIZE;
    const chartData = filledData
        .map((item) => {
        const date = new Date(item.date);
        return {
            date: formatDateMMDD(date),
            tooltipDate: formatTooltipDate(date, timezone),
            value: item.energyDeliveredToSite === 0 ? NA_VALUE : item.energyDeliveredToSite
        };
    })
        .reverse(); // latest date first
    // dischargeChartData only used for showDischargeEnergy=false path (MethanolBarChart)
    return (_jsx("div", { "data-test": "methanol-energy-bar-chart-container", children: showDischargeEnergy ? (
        // Old-style full-width Plotly chart matching legacy AssetPageDetail Energy Use History
        _jsxs(_Fragment, { children: [_jsx(BarChart, { autosize: true, config: { displayModeBar: false, staticPlot: false }, data: [
                        {
                            hovertemplate: "%{y}<extra></extra>",
                            marker: { color: "#1AC69E" },
                            name: "Charge",
                            type: "bar",
                            x: filledData.map((d) => {
                                const dt = new Date(d.date);
                                return `${dt.getUTCDate()} ${dt.toLocaleString("en-US", { month: "long", timeZone: "UTC" })}`;
                            }),
                            y: filledData.map((d) => d.energyDeliveredToSite === NA_VALUE ? null : d.energyDeliveredToSite)
                        },
                        {
                            hovertemplate: "%{y}<extra></extra>",
                            marker: { color: "#FB5A00" },
                            name: "Discharge",
                            type: "bar",
                            x: (filledDischargeData ?? filledData).map((d) => {
                                const dt = new Date(d.date);
                                return `${dt.getUTCDate()} ${dt.toLocaleString("en-US", { month: "long", timeZone: "UTC" })}`;
                            }),
                            y: (filledDischargeData ?? []).map((d) => d.energyDeliveredToSite === NA_VALUE ? null : d.energyDeliveredToSite)
                        }
                    ], layout: {
                        autosize: true,
                        barcornerradius: 3,
                        height: 450,
                        margin: { b: 50, l: 100, pad: 0, r: 30, t: 10 },
                        responsive: true,
                        showlegend: false,
                        xaxis: { tickangle: -45, type: "category" },
                        yaxis: { ticksuffix: " kWh" }
                    } }), _jsxs("div", { className: "flex w-full items-center justify-center mt-2", "data-test": "methanol-energy-bar-chart-legend", children: [_jsx("span", { className: "text-md mr-2", children: "Legend:" }), [
                            { color: "#1AC69E", label: "Charge" },
                            { color: "#FB5A00", label: "Discharge" }
                        ].map((item) => (_jsxs("div", { className: "flex items-center mr-4 whitespace-nowrap", children: [_jsx("div", { className: "w-2.5 h-2.5 rounded-full mr-1", style: { backgroundColor: item.color } }), _jsx("span", { children: item.label })] }, item.label)))] })] })) : (_jsx(MethanolBarChart, { barColor: BAR_COLOR, data: chartData, isEnergyBarChart: true, legendLabel: legendLabel || "kWh", testId: "methanol-energy-bar-chart", unit: ENERGY_UNIT, xAxisLabel: xAxisLabel, yAxisMax: maxEnergy, yAxisStepSize: yAxisStepSize })) }));
};
export default MethanolEnergyBarChart;
